import { useState } from 'react';

import {
  FormControlLabel,
  formControlLabelClasses,
  styled,
} from '@mui/material';

import { CAST_AI } from '@cast/constants';
import {
  Input,
  Radio,
  RadioGroup,
  Typography,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import { RosaClusterOnboardingParameters } from 'types/cluster';

export const useRosaParametersForm = () => {
  const [sccEnabled, setSccEnabled] = useState(false);
  const [rosaParameters, setRosaParameters] = useState<
    RosaClusterOnboardingParameters | undefined
  >();

  return {
    rosaFormProps: {
      sccEnabled,
      setSccEnabled,
      setRosaParameters,
      rosaParameters,
    },
    rosaQueryArg: sccEnabled ? rosaParameters : undefined,
  };
};

const StyledLabel = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: 'unset',
  [`& .${formControlLabelClasses.label}`]: {
    ...theme.typography.A2,
    paddingLeft: '8px',
  },
}));

type SetRosaParameters = (
  rosaParameters?: RosaClusterOnboardingParameters
) => RosaClusterOnboardingParameters | undefined;

type Props = {
  sccEnabled: boolean;
  setSccEnabled: (enabled: boolean) => void;
  setRosaParameters: (setRosaParameters: SetRosaParameters) => void;
  rosaParameters: RosaClusterOnboardingParameters | undefined;
};

export const RosaParametersForm = ({
  sccEnabled,
  setSccEnabled,
  setRosaParameters,
  rosaParameters,
}: Props) => {
  const renderInput = (param: keyof RosaClusterOnboardingParameters) => (
    <Input
      testId={`rosa-${param}-input`}
      fullWidth
      size="small"
      label={param}
      defaultValue={rosaParameters?.[param]}
      onBlur={(e) => {
        setRosaParameters((params) => ({
          ...params,
          [param]: e.target.value || undefined,
        }));
      }}
    />
  );

  return (
    <Stack gap="16px" data-testid="scc-setup-section">
      <Stack gap="14px">
        <Stack gap="4px">
          <Typography variant="h6">SCC setup on the cluster</Typography>

          <Typography variant="P12R">
            Do you use a custom Security Context Constraints setup?
            <br />
            If so, additional parameters will need to be specified.
          </Typography>
        </Stack>

        <RadioGroup
          value={sccEnabled}
          onChange={(_, value) => setSccEnabled(value === 'true')}
          sx={{ flexDirection: 'row', mb: '6px' }}
        >
          <StyledLabel
            value={false}
            control={<Radio size="small" testId="ssc-disabled-option" />}
            label="No"
          />
          <StyledLabel
            value={true}
            control={<Radio size="small" testId="ssc-enabled-option" />}
            label="Yes"
          />
        </RadioGroup>
      </Stack>

      {sccEnabled && (
        <Stack
          bgcolor={colorOrToken(
            'grey.100',
            tokens.theme.bg.structural.surfaceSubtle
          )}
          mx="-32px"
          p="12px 32px 16px"
          gap="24px"
        >
          <Stack gap="8px">
            <Typography variant="A2">
              Provide SCC IDs to be used by the {CAST_AI} agent
            </Typography>

            <Stack direction="row" justifyContent="space-between" gap="16px">
              {renderInput('runAsUser')}
              {renderInput('runAsGroup')}
              {renderInput('fsGroup')}
            </Stack>
          </Stack>

          <div>
            <Typography variant="A2" mb="6px">
              Add {CAST_AI} service account to SCC users
            </Typography>
            <Typography
              variant="P12R"
              color={colorOrToken(
                'grey.700',
                tokens.theme.fg.structural.secondary
              )}
            >
              In scenarios where access to SCC is restricted, please update the
              relevant SCC to let the castai-agent account use it.
            </Typography>
          </div>
        </Stack>
      )}
    </Stack>
  );
};
